import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { N_ } from '../i18n';

// Labels are translation keys: display them with t(preset.label)
export const DATE_PRESETS: { key: string; label: string; getRange: () => { start: string; end: string } | null }[] = [
  { key: 'today', label: N_('Bugün'), getRange: () => ({ start: dayjs().format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { key: 'yesterday', label: N_('Dün'), getRange: () => ({ start: dayjs().subtract(1, 'day').format('YYYY-MM-DD'), end: dayjs().subtract(1, 'day').format('YYYY-MM-DD') }) },
  { key: 'this_week', label: N_('Bu hafta (Pzt-Bugün)'), getRange: () => ({ start: dayjs().startOf('week').add(1, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { key: 'last_7', label: N_('Son 7 gün'), getRange: () => ({ start: dayjs().subtract(7, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { key: 'last_week', label: N_('Geçen hafta (Pzt – Paz)'), getRange: () => ({ start: dayjs().subtract(1, 'week').startOf('week').add(1, 'day').format('YYYY-MM-DD'), end: dayjs().subtract(1, 'week').endOf('week').add(1, 'day').format('YYYY-MM-DD') }) },
  { key: 'last_14', label: N_('Son 14 gün'), getRange: () => ({ start: dayjs().subtract(14, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { key: 'this_month', label: N_('Bu ay'), getRange: () => ({ start: dayjs().startOf('month').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { key: 'last_30', label: N_('Son 30 gün'), getRange: () => ({ start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { key: 'last_month', label: N_('Geçen ay'), getRange: () => ({ start: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') }) },
  { key: 'last_90', label: N_('Son 90 gün'), getRange: () => ({ start: dayjs().subtract(90, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') }) },
  { key: 'all', label: N_('Tüm zamanlar'), getRange: () => null },
];

interface GoogleAdsDateContextType {
  datePreset: string;
  startDate: string;
  endDate: string;
  setDateFilter: (preset: string, start: string, end: string) => void;
}

const GoogleAdsDateContext = createContext<GoogleAdsDateContextType | null>(null);

function calcDatesFromPreset(preset: string): { start: string; end: string } {
  const p = DATE_PRESETS.find(d => d.key === preset);
  if (!p) return { start: dayjs().subtract(30, 'day').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') };
  const range = p.getRange();
  return range || { start: '', end: '' };
}

function loadInitial(): { preset: string; startDate: string; endDate: string } {
  try {
    const saved = localStorage.getItem('google_ads_date_preset');
    if (saved) {
      const preset = saved;
      if (preset === 'custom') {
        const s = localStorage.getItem('google_ads_date_custom_start') || '';
        const e = localStorage.getItem('google_ads_date_custom_end') || '';
        return { preset, startDate: s, endDate: e };
      }
      const { start, end } = calcDatesFromPreset(preset);
      return { preset, startDate: start, endDate: end };
    }
  } catch {}
  const { start, end } = calcDatesFromPreset('last_30');
  return { preset: 'last_30', startDate: start, endDate: end };
}

export const GoogleAdsDateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initial = useMemo(() => loadInitial(), []);
  const [datePreset, setDatePreset] = useState(initial.preset);
  const [startDate, setStartDate] = useState(initial.startDate);
  const [endDate, setEndDate] = useState(initial.endDate);

  const setDateFilter = useCallback((preset: string, start: string, end: string) => {
    setDatePreset(preset);
    setStartDate(start);
    setEndDate(end);
    try {
      localStorage.setItem('google_ads_date_preset', preset);
      if (preset === 'custom') {
        localStorage.setItem('google_ads_date_custom_start', start);
        localStorage.setItem('google_ads_date_custom_end', end);
      } else {
        localStorage.removeItem('google_ads_date_custom_start');
        localStorage.removeItem('google_ads_date_custom_end');
      }
    } catch {}
  }, []);

  const value = useMemo(() => ({ datePreset, startDate, endDate, setDateFilter }), [datePreset, startDate, endDate, setDateFilter]);

  return (
    <GoogleAdsDateContext.Provider value={value}>
      {children}
    </GoogleAdsDateContext.Provider>
  );
};

export function useGoogleAdsDate(): GoogleAdsDateContextType {
  const ctx = useContext(GoogleAdsDateContext);
  if (!ctx) throw new Error('useGoogleAdsDate must be used within GoogleAdsDateProvider');
  return ctx;
}
